import React from 'react';
import { useIntl } from '@leagueplatform/locales';
import type { EntityListTableColumnHeader } from '@web-config-app/core';
import { getLabelText } from '@web-config-app/core-react';
import {
  Icon,
  InputLabel,
  CheckboxIndicator,
  VisuallyHidden,
} from '@leagueplatform/genesis-core';
import type { GDSStyleObject } from '@leagueplatform/genesis-core';
import type { EntityListTableSelectionMode } from '../../../types/components.types';
import { TableHeading } from '../table-pieces.component';

interface EntityListTableHeadersProps {
  columns: Array<EntityListTableColumnHeader>;
  tableHeadingCss?: GDSStyleObject;
  selectionMode: EntityListTableSelectionMode;
  selectionState?: {
    allSelected: boolean;
    indeterminate: boolean;
    onSelectAllChange: (checked: boolean) => void;
    disabled: boolean;
  };
}

export const EntityListTableHeaders: React.FC<EntityListTableHeadersProps> = ({
  columns,
  tableHeadingCss,
  selectionMode = 'none',
  selectionState,
}) => {
  const { formatMessage } = useIntl();

  return (
    <thead>
      <tr>
        {selectionMode === 'single' && (
          <TableHeading
            css={{
              width: '$twoAndHalf',
              textAlign: 'center',
              ...tableHeadingCss,
            }}
          >
            <Icon
              icon="interfaceCheckmark"
              size="$one"
              label={formatMessage({
                id: 'SELECTABLE_TABLE_ROW_STATUS_HEADING',
              })}
            />
          </TableHeading>
        )}
        {selectionMode === 'multi' && selectionState?.onSelectAllChange && (
          <TableHeading
            css={{
              width: '$twoAndHalf',
              textAlign: 'center',
              ...tableHeadingCss,
            }}
          >
            <VisuallyHidden>
              <InputLabel htmlFor="select-all">Select all</InputLabel>
            </VisuallyHidden>
            <CheckboxIndicator
              id="select-all"
              name="select-all"
              disabled={selectionState.disabled}
              checked={selectionState.allSelected}
              indeterminate={selectionState.indeterminate}
              onChange={(evt) => {
                selectionState.onSelectAllChange(evt.target.checked);
              }}
            />
          </TableHeading>
        )}

        {columns.map((col) => {
          const headingCss = {
            width: col.width || (col.hideLabel ? '$four' : undefined),
            ...tableHeadingCss,
          };
          const labelText = getLabelText(formatMessage, col.label);
          return (
            <TableHeading key={col.id} css={headingCss}>
              {col.hideLabel ? (
                <VisuallyHidden>{labelText}</VisuallyHidden>
              ) : (
                labelText
              )}
            </TableHeading>
          );
        })}
      </tr>
    </thead>
  );
};
